import { useEffect, useState, useRef } from "react";

export default function Irssei() {
  const [quote, setQuote] = useState(0);
  const [fade, setFade] = useState(true);
  const [soundOn, setSoundOn] = useState(false);
  const audioRef = useRef(null);
  const gainNodeRef = useRef(null);
  const contextRef = useRef(null);

  const echoes = [
    "Je l’ai vue dans mes rêves avant de la voir sur le mur.",
    "Certains visages parlent encore quand la peinture s’efface.",
    "Il reste ce qu’on oublie, pas ce qu’on montre.",
    "Chaque fragment est une mémoire qui n’a pas survécu.",
    "Ce qui reste est ce qui n’a jamais été dit."
  ];

  useEffect(() => {
    let timeout1, timeout2;

    const startCycle = () => {
      setFade(false);
      timeout1 = setTimeout(() => {
        setQuote((prev) => (prev + 1) % echoes.length);
        setFade(true);
        timeout2 = setTimeout(startCycle, 6000);
      }, 3000);
    };

    startCycle();
    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
    };
  }, []);

  const toggleSound = () => {
    if (audioRef.current) {
      if (!soundOn) {
        contextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        const source = contextRef.current.createMediaElementSource(audioRef.current);
        gainNodeRef.current = contextRef.current.createGain();
        source.connect(gainNodeRef.current).connect(contextRef.current.destination);
        audioRef.current.play();

        const modulate = () => {
          if (!gainNodeRef.current) return;
          const nextVolume = 0.2 + Math.random() * 0.3;
          gainNodeRef.current.gain.linearRampToValueAtTime(nextVolume, contextRef.current.currentTime + 5);
          setTimeout(modulate, 5000);
        };
        modulate();
      } else {
        audioRef.current.pause();
        if (contextRef.current) contextRef.current.close();
      }
      setSoundOn(!soundOn);
    }
  };

  const fragments = [
    {
      code: "LMD.01_BR",
      text: "Le monde s'efface sous ses traits craquelés.",
      image: "/lmd1.png",
      story: "Sa peau fissurée raconte l’érosion des rêves, témoin d’un temps qu’on ne peut retenir."
    },
    {
      code: "LMD.02_HT",
      text: "Un éclat de couleur perce l’ombre d’un regard.",
      image: "/lmd2.png",
      story: "Les taches vives sur le mur noir révèlent une étincelle d’espoir dans le chaos urbain."
    },
    {
      code: "LMD.03_CX",
      text: "Sa révolte se mêle aux graffiti des rues.",
      image: "/lmd6.png",
      story: "Le murmure de la foule se reflète dans ses lunettes, comme un écho de contestation gravé sur béton."
    },
    {
      code: "LMD.04_øR",
      text: "Chaque fissure est une cicatrice d’histoire.",
      image: "/lmd3.png",
      story: "Son visage sculpté dans la poussière évoque les mémoires égarées entre les couches de peinture."
    },
    {
      code: "LMD.05_∆Y",
      text: "Un souffle de couleur sur un monde en ruine.",
      image: "/lmd5.png",
      story: "Sous son bonnet bariolé, le silence crie plus fort que mille bombes de peinture."
    },
    {
      code: "LMD.06_∞Z",
      text: "Le béton s’anime d’une âme fragile.",
      image: "/lmd4.png",
      story: "Les éclaboussures fluo dansent autour de son expression, gardienne d’un rêve qui vacille."
    }
  ];

  return (
    <main className="relative min-h-screen bg-neutral-950 text-neutral-100 font-sans px-6 py-12 overflow-hidden">
      <audio ref={audioRef} loop style={{ display: 'none' }}>
        <source src="/irssei-souffle.mp3" type="audio/mpeg" />
        Votre navigateur ne supporte pas l'audio.
      </audio>

      <button
        onClick={toggleSound}
        className="fixed bottom-4 right-4 z-50 text-xs uppercase border border-neutral-600 px-3 py-1 bg-neutral-900 text-neutral-300 hover:bg-neutral-800"
      >
        {soundOn ? "Son : activé" : "Son : coupé"}
      </button>

      <div
        className="absolute inset-0 z-0 bg-cover bg-center pointer-events-none"
        style={{ backgroundImage: "url('/texture-noire.png')" }}
      >
        <div
          className="absolute inset-0 bg-center bg-repeat opacity-10 mix-blend-overlay blur-sm animate-fade-in"
          style={{ backgroundImage: "url('/irssei-alphabet.png')" }}
        />
      </div>

      <section className="relative z-10 flex flex-col items-center justify-center text-center h-screen">
        <div className="mb-6">
          <p
            className={`italic text-neutral-500 text-lg transition-opacity duration-[3000ms] ease-in-out tracking-wide ${fade ? "opacity-100" : "opacity-0"}`}
          >
            {`“${echoes[quote]}”`}
          </p>
        </div>
        <h1 className="text-4xl md:text-6xl tracking-wide uppercase fade-in-down animate-fade-in-down">
          Quelqu'un a perdu son rêve ici.
        </h1>
        <a
          href="#fragments"
          className="mt-10 px-6 py-2 border border-neutral-500 hover:border-neutral-300 text-sm tracking-widest uppercase"
        >
          Voir
        </a>
      </section>

      <section id="fragments" className="relative z-10 mt-32 space-y-28 max-w-5xl mx-auto">
        <div className="text-center text-neutral-500 uppercase tracking-widest mb-16 animate-pulse text-xs">— fragments —</div>
        {fragments.map(({ code, text, image, story }, i) => (
          <div
            key={i}
            className={`flex flex-col md:flex-row ${i % 2 === 1 ? 'md:flex-row-reverse' : ''} gap-8 items-center`}
          >
            <div className="w-full md:w-1/2 relative">
              <img
                src={image}
                alt={`Fragment ${i + 1}`}
                className="rounded-xl shadow-lg grayscale transition-all duration-1000 ease-in-out hover:grayscale-0 hover:saturate-150"
              />
              <div className="absolute bottom-2 right-4 text-sm text-neutral-400 uppercase tracking-widest">
                {code}
              </div>
            </div>
            <div className="w-full md:w-1/2">
              <p className="text-neutral-400 italic leading-relaxed text-lg whitespace-pre-line animate-fade-in-up mb-2">
                {text}
              </p>
              <p className="text-sm text-neutral-500 italic">{story}</p>
            </div>
          </div>
        ))}
      </section>

      <section className="relative z-10 mt-32 max-w-4xl mx-auto border-t border-neutral-800 pt-12 animate-fade-in-up">
        <h2 className="text-3xl mb-6 uppercase tracking-widest text-center text-neutral-300">Manifeste</h2>
        <div className="text-neutral-400 leading-relaxed text-lg font-serif space-y-4">
          {[
            "Je ne suis pas une signature.",
            "Je suis une absence.",
            "Irssei n’est ni un nom, ni une personne. Irssei est ce qui reste quand le rêve s’effondre.",
            "Je peins les visages qui se perdent. Les enfants qu’on oublie, les femmes qu’on efface, les regards qu’on croise trop vite.",
            "Chaque œuvre est un murmure. Un pochoir sur le mur du temps. Une trace laissée par une mémoire qui n’a pas demandé à survivre.",
            "Je ne parle pas. Je ne montre pas.",
            "Irssei existe là où la lumière hésite. Là où le grain de la peau devient terrain. Là où le silence devient cri.",
            "— Irssei (fragment 0)"
          ].map((line, i) => (
            <p
              key={i}
              className="opacity-60 blur-sm hover:blur-none hover:opacity-100 transition-all duration-1000 ease-in-out animate-mystery"
            >
              {line}
            </p>
          ))}
        </div>
      </section>
    </main>
  );
}
